import React from 'react';
import { ExternalLink } from 'lucide-react';
import { processReportContent } from '../../pages/reportHtmlParser';
import { formatContent, filterAICategory, processInlineMarkdown } from './markdownUtils';
import { convertLatexToUnicode } from './latexUtils';

/**
 * Alpha Hot Papers 리포트 렌더링 컴포넌트
 */
const AlphaHotPapersReportContent = ({ report, itemActions }) => {
    if (!report.report_content.papers || !Array.isArray(report.report_content.papers)) {
        return (
            <div className="p-4 text-gray-500">
                논문 데이터를 찾을 수 없습니다.
            </div>
        );
    }

    const papers = report.report_content.papers;
    const reportDesc = report.report_content.report_desc;

    const extractTags = (summary) => {
        if (!summary) return [];
        // "📌 Tags: tag1, tag2, tag3" 형식에서 태그 추출
        const tagMatch = summary.match(/📌\s*Tags?\s*:\s*([^\n]+)/i);
        if (tagMatch) {
            return tagMatch[1].split(',').map(tag => tag.trim());
        }
        return [];
    };

    const renderRankInfo = (paper) => {
        // extra_text_desc에서 순위 정보 추출
        if (!paper.extra_text_desc) {
            return null;
        }

        const extraDesc = paper.extra_text_desc;
        // "🏆 *현재 순위*: 1 (↑9)" 형식 파싱
        const rankMatch = extraDesc.match(/현재 순위.*?:?\s*(\d+)/);
        const changeMatch = extraDesc.match(/(↑|↓)(\d+)/);

        if (!rankMatch) {
            return null;
        }

        const rank = rankMatch[1];
        let changeText = null;
        let changeColor = 'text-gray-600';

        if (changeMatch) {
            const direction = changeMatch[1];
            const amount = changeMatch[2];
            changeColor = direction === '↑' ? 'text-green-600' : 'text-red-600';
            changeText = `(${direction}${amount})`;
        }

        // summary에서 태그 추출
        const tags = extractTags(paper.summary);

        return (
            <div className="text-sm text-gray-700 mb-2 ml-8 flex items-center gap-2 flex-wrap">
                <span className="font-medium">🏆 현재 순위:</span>
                <span className="font-semibold">{rank}위</span>
                {changeText && (
                    <span className={`font-medium ${changeColor}`}>
                        {changeText}
                    </span>
                )}
                {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 ml-2">
                        {tags.map((tag, tagIdx) => (
                            <span key={tagIdx} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                {tag}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderSummary = (summary) => {
        if (!summary) return null;

        // Tags 라인과 TL;DR 라인 제거
        const lines = summary.split('\n').filter(line => {
            const trimmed = line.trim();
            return trimmed &&
                   !trimmed.match(/^📌\s*Tags?\s*:/i) &&
                   !trimmed.match(/^💡\s*TL;DR\s*:?\s*$/i);
        });

        const formattedLines = lines.map((line, lineIdx) => {
            // [문제점], [해결책], [세부설명] 등의 대괄호 패턴을 감지
            const bracketMatch = line.match(/^(\[.*?\])\s*(.*)$/);
            if (bracketMatch) {
                const [, label, content] = bracketMatch;
                return (
                    <div key={lineIdx} className="mb-2">
                        <span className="font-semibold text-gray-800">{label}</span>{' '}
                        <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: processInlineMarkdown(content) }} />
                    </div>
                );
            }
            return <div key={lineIdx} dangerouslySetInnerHTML={{ __html: processInlineMarkdown(line) }} />;
        });

        return (
            <div className="mt-3 ml-8">
                <div className="text-sm text-gray-600">
                    {formattedLines}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* 리포트 통합 요약 */}
            {reportDesc && (
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 rounded">
                    <div className="text-sm text-gray-800 whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: processInlineMarkdown(reportDesc) }}
                    />
                </div>
            )}

            {papers.map((paper, idx) => (
                <div key={idx} className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                    {/* 논문 번호와 제목 */}
                    <div className="flex items-start mb-3">
                        <span className="inline-block font-bold text-xl mr-3 flex-shrink-0 text-blue-600">
                            {idx + 1}.
                        </span>
                        <h3 className="font-semibold text-lg flex-1">
                            {convertLatexToUnicode(paper.title)}
                        </h3>
                    </div>

                    {/* 저자 */}
                    {paper.authors && paper.authors.length > 0 && (
                        <p className="text-sm text-gray-600 mb-2 ml-8">
                            {paper.authors.join(', ')}
                        </p>
                    )}

                    {/* arXiv 링크 */}
                    {paper.arxiv_id && (
                        <a
                            href={`https://arxiv.org/abs/${paper.arxiv_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm mb-2 ml-8"
                        >
                            arXiv: {paper.arxiv_id}
                            <ExternalLink className="w-3 h-3" />
                        </a>
                    )}

                    {/* 순위 정보 */}
                    {renderRankInfo(paper)}

                    {/* 요약 (TL;DR 포맷 개선) */}
                    {renderSummary(paper.summary)}

                    {itemActions?.({ source_type: 'paper', source_id: paper.arxiv_id })}
                </div>
            ))}
        </div>
    );
};

/**
 * Personal News 리포트 렌더링 컴포넌트
 */
const PersonalNewsReportContent = ({ report, itemActions }) => {
    if (!report.report_content.articles || !Array.isArray(report.report_content.articles)) {
        return (
            <div className="p-4 text-gray-500">
                개인 맞춤 뉴스 데이터를 찾을 수 없습니다.
            </div>
        );
    }

    const articles = report.report_content.articles;
    const userTags = report.report_content.user_tags || [];

    return (
        <div className="space-y-4">
            {/* 사용자 관심 태그 표시 */}
            {userTags.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-md">
                    <div className="text-sm font-medium text-blue-800 mb-2">관심 태그</div>
                    <div className="flex flex-wrap gap-2">
                        {userTags.map((tag, idx) => (
                            <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                #{tag}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* 뉴스 기사 목록 */}
            <div className="space-y-3">
                {articles.map((article, idx) => (
                    <div key={idx} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium text-lg flex-1" style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}>
                                <a
                                    href={article.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-gray-900 hover:text-primary-600 transition-colors"
                                >
                                    {article.title}
                                </a>
                            </h3>
                            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                {article.relevance_score && (
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                        매칭 {Number(article.relevance_score).toFixed(1)}
                                    </span>
                                )}
                                <ExternalLink className="w-4 h-4 text-gray-400" />
                            </div>
                        </div>

                        {article.summary && (
                            <p className="text-gray-600 text-sm mb-3"
                                dangerouslySetInnerHTML={{ __html: processInlineMarkdown(article.summary) }}
                            />
                        )}

                        <div className="flex flex-wrap justify-between items-center text-xs text-gray-500">
                            <div className="flex items-center gap-4">
                                <span>📰 {article.source}</span>
                                <span>📅 {new Date(article.date).toLocaleDateString('ko-KR')}</span>
                                {article.match_type && (
                                    <span className="px-2 py-1 bg-gray-100 rounded">
                                        {article.match_type === 'tag_match' ? '태그 일치' : article.match_type}
                                    </span>
                                )}
                            </div>
                        </div>

                        {itemActions?.({
                            source_type: 'article',
                            source_id: String(article.article_id ?? article.id ?? ''),
                            source: article.source,
                        })}
                    </div>
                ))}
            </div>

            {/* 요약 정보 */}
            <div className="mt-4 p-3 bg-gray-50 rounded-md text-sm text-gray-600">
                총 {articles.length}개의 맞춤 뉴스가 추천되었습니다.
                {report.report_content.article_count && report.report_content.article_count !== articles.length && (
                    <span className="ml-2">(전체 {report.report_content.article_count}개 중)</span>
                )}
            </div>
        </div>
    );
};

/**
 * 카테고리별 리포트 렌더링 컴포넌트
 */
const CategoryReportContent = ({ categories, reportId, expandedCategories, onToggleCategory, itemActions }) => {
    // 카테고리가 1개만 있으면 wrapping 없이 바로 내용 표시
    if (categories.length === 1) {
        const category = categories[0];
        return (
            <div>
                {category.papers ? (
                    // 표준화된 HTML 형식
                    category.papers.map((paper, paperIdx) => (
                        <div key={`single-paper-${paperIdx}`} className="mb-4">
                            <h3 className="font-bold">{convertLatexToUnicode(paper.title)}</h3>
                            {paper.authors && <p className="text-sm text-gray-600">{paper.authors}</p>}

                            {paper.summary && paper.summary.length > 0 && (
                                <div className="mt-2">
                                    {paper.summary.map((section, sectionIdx) => (
                                        <div key={`single-paper-${paperIdx}-section-${sectionIdx}`} className="mb-2">
                                            <p><strong>{section.title}:</strong> <span dangerouslySetInnerHTML={{ __html: processInlineMarkdown(section.content) }} /></p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {paper.tags && paper.tags.length > 0 && (
                                <p className="text-sm text-gray-600 mt-1">
                                    📌 Tags: {paper.tags.join(', ')}
                                </p>
                            )}

                            {paper.arxiv_id && (
                                <p className="text-sm text-primary-600 mt-1">
                                    <a
                                        href={`https://arxiv.org/abs/${paper.arxiv_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1"
                                    >
                                        arXiv: {paper.arxiv_id}
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </p>
                            )}

                            {itemActions?.({ source_type: 'paper', source_id: paper.arxiv_id })}
                        </div>
                    ))
                ) : (
                    // 카테고리 태그 분류 형식
                    formatContent(category.content)
                )}
            </div>
        );
    }

    // 카테고리가 여러 개일 때는 접을 수 있는 형태로 표시
    return (
        <>
            {categories.map((category, idx) => {
                const key = `${reportId}-${idx}`;
                return (
                    <div key={key} className="border-l-4 border-primary-500 pl-4 mb-4">
                        <div
                            className="flex justify-between items-center p-2 bg-gray-50 cursor-pointer"
                            onClick={(e) => onToggleCategory(reportId, idx, e)}
                        >
                            <span className="font-medium">
                                📁 {filterAICategory(category.categories).join(' + ')}
                            </span>
                            <span>{expandedCategories.has(key) ? '▼' : '▶'}</span>
                        </div>
                        {expandedCategories.has(key) && (
                            <div className="p-4">
                                {category.papers ? (
                                    // 표준화된 HTML 형식
                                    category.papers.map((paper, paperIdx) => (
                                        <div key={`${key}-paper-${paperIdx}`} className="mb-4">
                                            <h3 className="font-bold">{convertLatexToUnicode(paper.title)}</h3>
                                            {paper.authors && <p className="text-sm text-gray-600">{paper.authors}</p>}

                                            {paper.summary && paper.summary.length > 0 && (
                                                <div className="mt-2">
                                                    {paper.summary.map((section, sectionIdx) => (
                                                        <div key={`${key}-paper-${paperIdx}-section-${sectionIdx}`} className="mb-2">
                                                            <p><strong>{section.title}:</strong> <span dangerouslySetInnerHTML={{ __html: processInlineMarkdown(section.content) }} /></p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {paper.tags && paper.tags.length > 0 && (
                                                <p className="text-sm text-gray-600 mt-1">
                                                    📌 Tags: {paper.tags.join(', ')}
                                                </p>
                                            )}

                                            {paper.arxiv_id && (
                                                <p className="text-sm text-primary-600 mt-1">
                                                    <a
                                                        href={`https://arxiv.org/abs/${paper.arxiv_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1"
                                                    >
                                                        arXiv: {paper.arxiv_id}
                                                        <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                </p>
                                            )}

                                            {itemActions?.({ source_type: 'paper', source_id: paper.arxiv_id })}
                                        </div>
                                    ))
                                ) : (
                                    // 카테고리 태그 분류 형식
                                    formatContent(category.content)
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
};

/**
 * 리포트 콘텐츠 렌더링 메인 컴포넌트
 * @param {Object} props
 * @param {Object} props.report - 리포트 데이터
 * @param {Set} props.expandedCategories - 확장된 카테고리 Set
 * @param {Function} props.onToggleCategory - 카테고리 토글 핸들러
 * @param {Function} [props.itemActions] - 논문/기사 한 건 아래에 그릴 액션.
 *        ({source_type, source_id, source}) => node. 넘기지 않으면 아무것도 그리지 않는다 —
 *        웹 화면은 지금 그대로이고, Mini App 만 Easy Read 버튼을 얹는다.
 */
export const ReportRenderer = ({ report, expandedCategories, onToggleCategory, itemActions }) => {
    // YouTube URL 계산 (Easy Read 타임스탬프 링크용)
    const youtubeUrl = report.report_content?.source_type === 'youtube' && report.report_content?.source_id
        ? `https://www.youtube.com/watch?v=${report.report_content.source_id}`
        : null;

    // Personal News 리포트인 경우 전용 렌더링 사용
    if (report.agent_name === 'Personal News' && report.report_content.articles) {
        return <PersonalNewsReportContent report={report} itemActions={itemActions} />;
    }

    // Alpha Hot Papers 리포트인 경우 전용 렌더링 사용
    if (report.agent_name === 'Alpha Hot Papers' && report.report_content.papers) {
        return <AlphaHotPapersReportContent report={report} itemActions={itemActions} />;
    }

    // 보고서 내용 처리
    const processedContent = processReportContent(report.report_content);

    // 표준화된 HTML 형식이 파싱된 경우
    if (processedContent.parsed_data && processedContent.parsed_data.categories) {
        return (
            <CategoryReportContent
                categories={processedContent.parsed_data.categories}
                reportId={report.id}
                expandedCategories={expandedCategories}
                onToggleCategory={onToggleCategory}
                itemActions={itemActions}
            />
        );
    }

    // 카테고리 태그 분류가 있을 때
    if (processedContent.parsed_categories) {
        return (
            <CategoryReportContent
                categories={processedContent.parsed_categories}
                reportId={report.id}
                expandedCategories={expandedCategories}
                onToggleCategory={onToggleCategory}
                itemActions={itemActions}
            />
        );
    }

    // 레거시 콘텐츠가 있을 때
    if (processedContent.legacy_content) {
        return (
            <div className="whitespace-pre-wrap">
                {formatContent(processedContent.legacy_content, { youtubeUrl })}
            </div>
        );
    }

    // 기본 폴백 - report_content 형식에 따라 적절히 처리
    if (report.report_content.formatted_report) {
        return (
            <div className="whitespace-pre-wrap">
                {formatContent(report.report_content.formatted_report, { youtubeUrl })}
            </div>
        );
    } else if (report.report_content.formatted_content) {
        return (
            <div className="whitespace-pre-wrap">
                {formatContent(report.report_content.formatted_content, { youtubeUrl })}
            </div>
        );
    } else if (report.report_content.content) {
        return (
            <div className="whitespace-pre-wrap">
                {formatContent(report.report_content.content, { youtubeUrl })}
            </div>
        );
    }

    return (
        <div className="p-4 text-gray-500">
            보고서 내용을 표시할 수 없습니다.
        </div>
    );
};

export default ReportRenderer;
